import React from 'react';
import loadable from '@loadable/component';

import { dateFormatter } from 'app/shared/utils/datetime';
import {
  Accordion,
  AlertBanner,
  CompetitionHeroWithEventCard,
  ContentHero,
  CoreValues,
  CtaButton,
  Data,
  DataUrl,
  EventGrid,
  EventGridWithIds,
  FeaturedContent,
  FeaturedContentProps,
  FeaturedWebinar,
  Hero,
  HtmlText,
  IconImage,
  ImageBanner,
  ImageBleed,
  ImageFlow,
  InstagramLookAlike,
  Link,
  LiveStreamGrid,
  LiveStreamGridProps,
  LiveStreaming,
  PreviewCards,
  PreviewCardsProps,
  PrismicImage,
  QuickLinksNav,
  Slice,
  SpotifyPromotion,
  StaticGallery,
  TextHeaderLeft,
  VideoHighlights,
  WebinarHighlights,
  YoutubeEmbed,
} from 'app/shared/typings/prismic';

const mappings: {
  [key: string]: {
    component: string;
    mapDataToProps: (data: any) => any;
  };
} = {
  'image_-_full_bleed': {
    component: 'ResponsiveImage',
    mapDataToProps: (data: ImageBleed) => {
      return {
        src: data.image,
        alt: data.image_alt_text,
        caption: data.caption,
        imageSize: data.image_size,
      };
    },
  },
  // TODO: two different prismic implementation blog & everything else
  // need to consolidate both
  text: {
    component: 'HtmlText',
    mapDataToProps: (content: HtmlText) => {
      const { data, value } = content;

      if (value) {
        return {
          html: value,
          modal: content.modal_content,
          align: content.align_content,
          maxWidth: content.max_width,
          backgroundColor: content.background_color,
          adjustMarginTop: content.adjust_margin_top,
        };
      }

      return {
        align: data.align_content,
        html: data.value,
        modal: data.modal_content,
        maxWidth: data.max_width,
        backgroundColor: data.background_color,
        adjustMarginTop: data.adjust_margin_top,
      };
    },
  },
  'text_-_header_left': {
    component: 'HtmlTextWithLeftHeading',
    mapDataToProps: ({ header_left, content }: TextHeaderLeft) => ({
      heading: header_left,
      text: content,
    }),
  },
  options_grid: {
    component: 'RecommendedArticles',
    mapDataToProps: (data: any) => {
      return {
        title: data.title,
        articles: data.articles,
        align: data.align,
      };
    },
  },
  image_flow: {
    component: 'ImageFlow',
    mapDataToProps: (data: ImageFlow) => ({
      cards: data.image_group.map(({ icon, icon_copy }) => ({
        image: icon.main.url,
        title: icon_copy.blocks[0].text,
      })),
    }),
  },
  alert_banner: {
    component: 'AlertBanner',
    mapDataToProps: ({
      hero_copy,
      cta_link,
      cta_text,
      background_image,
    }: AlertBanner) => ({
      heroCopy: hero_copy,
      ctaLink: cta_link,
      ctaText: cta_text,
      backgroundImage: background_image,
    }),
  },
  banner_ad: {
    component: 'ImageBanner',
    mapDataToProps: ({ ad_image, cta_link }: ImageBanner) => ({
      ctaLink: cta_link,
      desktop: ad_image.desktop,
      mobile: ad_image.mobile,
      tablet: ad_image.tablet,
    }),
  },
  quick_links_nav: {
    component: 'QuickLinksNav',
    mapDataToProps: ({ links }: QuickLinksNav) => ({
      links: links.map(({ link_text, link }) => ({
        link,
        linkText: link_text,
      })),
    }),
  },
  core_values: {
    component: 'CoreValues',
    mapDataToProps: ({
      lead_in_title,
      lead_in_content,
      core_values,
    }: CoreValues) => ({
      leadInTitle: lead_in_title,
      leadInContent: lead_in_content,
      values: core_values,
    }),
  },
  cta_button: {
    component: 'CtaButton',
    mapDataToProps: ({ copy, link }: CtaButton) => ({
      copy,
      link,
    }),
  },
  youtube_embed: {
    component: 'YouTubePlayer',
    mapDataToProps: (data: YoutubeEmbed) => {
      return {
        url: data.value,
        size: data.size,
      };
    },
  },
  spotify_embed: {
    component: 'SpotifyPlayer',
    mapDataToProps: (data: any) => {
      return {
        url: data.playlist_url,
      };
    },
  },
  spotify_promotion: {
    component: 'SpotifyPromotion',
    mapDataToProps: ({
      image,
      copy,
      playlist_user,
      playlist_id,
      gradient_color_top,
      gradient_color_bottom,
      ctas,
    }: SpotifyPromotion) => ({
      image,
      copy,
      playlistUser: playlist_user,
      playlistId: playlist_id,
      gradientColorTop: gradient_color_top,
      gradientColorBottom: gradient_color_bottom,
      ctas,
    }),
  },
  static_gallery: {
    component: 'StaticGallery',
    mapDataToProps: ({
      image_1,
      image_2,
      image_3,
      image_4,
      image_5,
      image_6,
    }: StaticGallery) => ({
      image1: image_1,
      image2: image_2,
      image3: image_3,
      image4: image_4,
      image5: image_5,
      image6: image_6,
    }),
  },
  blog_newsletter_signup_form: {
    component: 'NewsletterForm',
    mapDataToProps: (data: any) => {
      return {
        header: data.form_header,
        subheader: data.form_subheader,
        submitButtonText: data.submit_button_text,
        successMessageHeader: data.success_header,
        successMessageSubheader: data.success_subheader,
      };
    },
  },
  artist_apply_form: {
    component: 'ArtistApplicationEntrypoint',
    mapDataToProps: () => {},
  },
  instagram_look_alike: {
    component: 'InstagramLookAlike',
    mapDataToProps: ({ title, copy, pictures = [] }: InstagramLookAlike) => ({
      title,
      copy,
      pictures: pictures.map(({ url, alt, link }) => ({
        src: url,
        alt,
        link,
      })),
    }),
  },
  content_hero: {
    component: 'ContentHero',
    mapDataToProps: ({
      title,
      sub_copy,
      image,
      cta,
      cta_link,
      content_direction,
      max_width,
    }: ContentHero) => ({
      title,
      subCopy: sub_copy,
      image,
      cta,
      ctaLink: cta_link,
      contentDirection: content_direction,
      maxWidth: max_width,
    }),
  },
  competition_hero_with_event_card: {
    component: 'CompetitionHeroWithEventCard',
    mapDataToProps: ({
      subheading,
      entered_copy,
      winner_copy,
      expired_copy,
      opt_in_copy,
      turn_on_opt_in,
      title,
      sub_copy,
      image,
      content_direction,
      max_width,
      event,
      city,
      country,
      venue,
      closed,
      competition_id,
      background_style,
      no_event_link,
      cta_color,
    }: CompetitionHeroWithEventCard) => ({
      subheading,
      enteredCopy: entered_copy,
      nonEnteredCopy: sub_copy,
      winnerCopy: winner_copy,
      expiredCopy: expired_copy,
      optInCopy: opt_in_copy,
      turnOnOptIn: turn_on_opt_in,
      title,
      image,
      contentDirection: content_direction,
      maxWidth: max_width,
      event,
      city,
      country,
      venue,
      closed,
      competitionId: competition_id,
      backgroundStyle: background_style,
      noEventLink: no_event_link,
      ctaColor: cta_color,
    }),
  },
  video_highlights: {
    component: 'VideoHighlights',
    mapDataToProps: ({
      title,
      copy,
      ctaLink,
      showCta,
      ctaText,
      videos = [],
    }: VideoHighlights) => ({
      title,
      copy,
      showCta,
      ctaLink,
      ctaText,
      videos,
      onClick: (to: string) => {
        if (typeof window !== 'undefined') {
          window.location.assign(to);
        }
      },
    }),
  },
  webinar_highlights: {
    component: 'WebinarHighlights',
    mapDataToProps: ({ videos }: WebinarHighlights) => ({
      videos,
    }),
  },
  featured_webinar: {
    component: 'FeaturedWebinar',
    mapDataToProps: (data: FeaturedWebinar) => ({
      date: data.date,
      header: data.webinar_header,
      title: data.webinar_title,
      description: data.description,
      url: data.embed_url,
    }),
  },
  icons_with_content: {
    component: 'IconsWithContent',
    mapDataToProps: (content: any) => {
      const data = (typeof content == 'object' && content.data) || content;
      const iconGroups = data.content.map(
        ({
          description,
          icon,
          title,
          button_link_text,
          button_link,
          link,
        }: {
          description: Data;
          icon: PrismicImage;
          title: Data;
          button_link_text: Data;
          button_link: DataUrl;
          link: DataUrl;
        }) => ({
          description:
            typeof description == 'undefined' ? undefined : description.value,
          title: typeof title == 'undefined' ? undefined : title.value,
          url: icon.main.url,
          buttonLinkText:
            typeof button_link_text == 'undefined' ? undefined : title.value,
          buttonLink:
            typeof button_link == 'undefined' ? undefined : button_link.url,
          link: typeof link == 'undefined' ? undefined : link.url,
        })
      );
      return {
        align: data.align,
        maxColumns: data.max_columns,
        width: data.width,
        title: data.title,
        iconSize: data.icon_size,
        backgroundColor: data.background_color,
        iconGroups,
      };
    },
  },
  icons_with_label: {
    component: 'IconGrid',
    mapDataToProps: (data: IconImage[]) => {
      const groups = data.map(({ icon, label }: any) => ({
        icon: icon.main.url,
        label: label.value,
      }));
      return { groups };
    },
  },
  showcase_image_grid_: {
    component: 'ImageGrid',
    mapDataToProps: (content: any) => {
      let data = (typeof content == 'object' && content.data) || content;

      const imageGroup = data.images.map(
        ({
          image,
          image_size,
          label,
          link,
        }: {
          image: PrismicImage;
          image_size: Data;
          label: Data;
          link: Link;
        }) => {
          return {
            desktop:
              image.views[
                `desktop_${image_size.value}` as keyof typeof image.views
              ].url,
            mobile:
              image.views[
                `mobile_${image_size.value}` as keyof typeof image.views
              ].url,
            size: image_size.value,
            label: label.value,
            link: link && link.url,
          };
        }
      );

      return { heading: data.heading, imageGroup };
    },
  },
  header_hero: {
    component: 'Hero',
    mapDataToProps: (content: Hero) => {
      const {
        lead_in_text,
        lead_in_image,
        cta_text,
        cta_link,
        image: { table },
        header,
        subheader,
        align,
        logo_img,
      } = content;
      return {
        leadInText: lead_in_text,
        leadInImage: lead_in_image ? lead_in_image.table.main : undefined,
        ctaText: cta_text,
        ctaLink: cta_link,
        img: {
          xs: table.mobile,
          sm: table.tablet,
          md: table.desktop_small,
          lg: table.desktop_medium,
          xl: table.desktop_large,
        },
        title: header,
        body: subheader,
        align,
        logoImg: logo_img ? logo_img.table.main : undefined,
      };
    },
  },
  hero_without_cta: {
    component: 'HeroWithoutCTA',
    mapDataToProps: (content: any) => {
      const data = (typeof content == 'object' && content.data) || content;

      return {
        images: {
          sm: data.image,
          md: data.image_medium,
          lg: data.image_large,
        },
        title: data.hero_copy,
      };
    },
  },
  platform_partial: {
    component: 'VenueApplicationEntrypoint',
    mapDataToProps: () => {},
  },
  platform_application_form: {
    component: 'ArtistApplicationEntrypoint',
    mapDataToProps: () => {},
  },
  banner_with_cta: {
    component: 'HostBanner',
    mapDataToProps: (content: any) => {
      const data = (typeof content == 'object' && content.data) || content;

      const bannerInfo = {
        heroCopy: data.hero_copy,
        subHeroCopy: data.sub_hero_copy,
        ctaCopy: data.cta_copy,
        ctaLink: data.cta_link,
        linkType: data.link_type,
      };

      return bannerInfo;
    },
  },
  testimonials: {
    component: 'Testimonials',
    mapDataToProps: (content: any) => {
      const data = (typeof content == 'object' && content.data) || content;

      const testimonials = data.map(
        ({ image, testimonial_text, job_title, name }: any) => ({
          name: name.value,
          jobTitle: job_title.value,
          testimonialText: testimonial_text.blocks[0].text,
          image: image.main.url,
        })
      );

      return { testimonials };
    },
  },
  event_grid: {
    component: 'EventGrid',
    mapDataToProps: (data: EventGrid) => ({
      cityName: data.city_title || data.city_slug,
      citySlug: data.city_slug,
      globallyPromoted: data.globally_promoted,
    }),
  },
  event_grid_with_ids: {
    component: 'EventGrid',
    mapDataToProps: (data: EventGridWithIds) => ({
      isMultiCity: true,
      eventIds: data.events || [],
    }),
  },
  live_streaming: {
    component: 'LiveStreaming',
    mapDataToProps: (data: LiveStreaming) => ({
      upcoming: data.upcoming_streams,
      past: data.past_streams,
    }),
  },
  live_streaming_platform: {
    component: 'LiveStreaming',
    mapDataToProps: (data: LiveStreaming) => ({
      upcoming: data.upcoming_streams,
      past: data.past_streams,
      displaySchedule: data.display_schedule,
    }),
  },
  spacer: {
    component: 'Spacer',
    mapDataToProps: () => ({}),
  },
  preview_cards: {
    component: 'PreviewCards',
    mapDataToProps: (content: PreviewCards): PreviewCardsProps => {
      return {
        previewCards: content.preview_cards,
        heading: content.heading,
        backgroundColor: content.background_color,
      };
    },
  },
  featured_content: {
    component: 'FeaturedContent',
    mapDataToProps: (content: FeaturedContent): FeaturedContentProps => {
      return {
        subheading: content.subheading,
        postedDate: content.date,
        alignImage: content.align_image,
        backgroundColor: content.background_color,
        overlineColor: content.overline_color,
        title: content.title,
        link: content.link,
        ctaText: content.cta_text,
        ctaIcon: content.cta_icon,
        ctaColor: content.cta_color,
        imageUrl: content.image,
        preview: content.preview,
        youtubeEmbed: content.youtube_url,
        headerLogoUrl: content.header_logo,
      };
    },
  },
  featured_livestream: {
    component: 'FeaturedContent',
    mapDataToProps: (content: FeaturedContent): FeaturedContentProps => {
      const subheading = content.date
        ? dateFormatter(
            content.date,
            'longWeekdayDateWithOrdinalFullMonthHoursMinutesWithAMPM'
          )
        : '';
      return {
        subheading,
        postedDate: content.date,
        alignImage: content.align_image,
        title: content.title,
        link: content.link,
        imageUrl: content.image,
        preview: content.preview,
        ctaText: 'View Livestream',
      };
    },
  },
  live_stream_grid: {
    component: 'LiveStreamGrid',
    mapDataToProps: (content: LiveStreamGrid): LiveStreamGridProps => {
      return {
        title: content.title,
        gridLayout: content.grid_layout,
        showInitially: content.show_initially,
        schedule: content.schedule,
        itemOffset: content.item_offset,
        loadMore: content.load_more,
      };
    },
  },

  collapsible_content_: {
    component: 'Accordion',
    mapDataToProps: (content: Accordion) => {
      return {
        section: content.content,
      };
    },
  },
};

export const mapResolver = (type: string) => {
  return mappings[type];
};

export const componentResolver = (slice: Slice) => {
  const resolvedComponent = mapResolver(slice.type);

  if (!resolvedComponent) {
    return null;
  }

  return resolvedComponent.component;
};

export const invertNavigation = (prismicData: any) => {
  let invertBoolean = false;
  for (let i = 0; i < prismicData.length; i++) {
    if (prismicData[i].type == 'header_hero') {
      invertBoolean = true;
      break;
    }
  }

  return invertBoolean;
};

export const renderPrismicContent = (prismicData: any, extraProps?: object) =>
  prismicData
    .map((slice: Slice, i: number) => {
      const prismicMapping = mapResolver(slice.type);
      const componentName = componentResolver(slice);
      const Component = loadable(
        () =>
          import(
            `./../../shared/components/organisms/Prismic/${componentName}/index.tsx`
          )
      );

      if (prismicMapping == undefined || !Component) {
        return;
      }

      const props = {
        ...prismicMapping.mapDataToProps(slice.data),
        ...extraProps,
      };

      return (
        <React.Fragment key={`${slice.type}-${i}`}>
          <Component {...props} />
        </React.Fragment>
      );
    })
    .filter(Boolean);
